/**
 * Fetches public pricing configuration from the FlowRide backend.
 */

import axios from "axios";

import { FALLBACK_CHF_TO_EUR_RATE, FALLBACK_WEAR_COST_PER_KM_CHF } from "../constants/pricing";

export interface PriceConfigurationDto {
    "defaultWearCostPerKmChf": number;
    "chfToEurRate":            number;
    "rateAsOf"?:               string;
    "exchangeRateIsLive":      boolean;
}

const isValidNumber = ( value: unknown ): value is number => {
    return typeof value === "number" && Number.isFinite( value ) && !Number.isNaN( value );
};

/**
 * @returns Parsed configuration, or fallbacks if the request fails or payload is invalid.
 */
export const fetchPriceConfiguration = async(): Promise<PriceConfigurationDto> => {
    try {
        const { data } = await axios.get<PriceConfigurationDto>( "/api/price-configuration", { "timeout": 10_000 } );

        const defaultWear = isValidNumber( data.defaultWearCostPerKmChf ) && data.defaultWearCostPerKmChf >= 0
            ? data.defaultWearCostPerKmChf
            : FALLBACK_WEAR_COST_PER_KM_CHF;
        const chfToEur = isValidNumber( data.chfToEurRate ) && data.chfToEurRate > 0
            ? data.chfToEurRate
            : FALLBACK_CHF_TO_EUR_RATE;

        return {
            "defaultWearCostPerKmChf": defaultWear,
            "chfToEurRate":            chfToEur,
            ...typeof data.rateAsOf === "string" ? { "rateAsOf": data.rateAsOf } : {},
            "exchangeRateIsLive":      data.exchangeRateIsLive === true,
        };
    } catch( error: unknown ) {
        console.warn( "[PriceConfigurationApi] Using fallback pricing configuration", error );

        return {
            "defaultWearCostPerKmChf": FALLBACK_WEAR_COST_PER_KM_CHF,
            "chfToEurRate":            FALLBACK_CHF_TO_EUR_RATE,
            "exchangeRateIsLive":      false,
        };
    }
};
